/**
 * Manifest review — the morning that turns yesterday's provider visit into
 * today's leads.
 *
 * Isaiah Cotto, 2026-09-23: "It is imperative we get the Manifest lead system
 * running to our operations so we can get submittals running again."
 *
 * == What was missing ====================================================
 *
 * The six routes have existed since 2026-09-21. The reviewer role has existed
 * since 2026-09-21. 33 buildings and 4,419 resident lines have been waiting.
 * What did not exist was a screen — so the whole lead origin was reachable
 * only by an engineer with a shell, and the distribution ring, which now
 * requires a manifest-confirmed lead, had nothing to distribute.
 *
 * == The shape of the work ===============================================
 *
 * Left: the buildings whose manifests are waiting, newest visit first, with
 * how much of each is decided. Right: the residents eMerge holds for the one
 * she opened, each a row with one decision on it.
 *
 * Undecided lines sort first, because the undecided ones are the work. A
 * decided line stays visible rather than disappearing — a reviewer must be
 * able to see and change what she recorded five minutes ago.
 *
 * == Two acts, kept apart ================================================
 *
 * Recording a decision decides nothing downstream. Only "Send to a Patient
 * Advocate" creates work for somebody, it is offered only on a confirmed
 * TWL, and it is gated by its own capability — a reviewer may hold the first
 * without the second, and when she does the button is absent rather than
 * broken.
 *
 * == A held lead is recorded, never discarded ============================
 *
 * When the eligibility engine holds a confirmed TWL, the intake exists, the
 * case exists, and this screen says which fact is missing. That is the
 * difference between somebody fixing it this morning and nobody knowing.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import WorkspaceMenu from '../../components/WorkspaceMenu';
import { WorkspaceLayout, SidebarTrigger, type SidebarGroup, type SidebarItem } from '../../components/workspaceSidebar';
import { ViewTransition } from '../../components/shadcn/motion';
import { Building2, CalendarClock, ClipboardList, Ear, FolderOpen, MapPinned, RotateCcw, SearchCheck, UserPlus, UserSearch, Users } from 'lucide-react';
import {
  Disposition, DISPOSITION_LABEL, DISPOSITION_MEANS, DISPOSITION_TONE,
  FacilityOption, IntakeResult, ManifestIndex, ResidentLine, ReviewDetail,
  TwlDayResult, manifestApi, reasonWords,
  OtherBuilding, ProviderChoice, WorkableReviewRef, WorkableRow,
} from './manifestApi';
import LeadDesk from './LeadDesk';
import ReplacementDesk from './ReplacementDesk';
import ResubmittalsDesk from './ResubmittalsDesk';
import VerificationDesk from './VerificationDesk';
import NoBuildingDesk from './NoBuildingDesk';
import ArrivalReviewsDesk, { reviewsArrivals } from './ArrivalReviewsDesk';
import { residentsApi, type IdentityScope } from '../advocate/residentsApi';
import UnassignedDesk from './UnassignedDesk';
// 2026-10-27: Facility records (docs/facility-delivery/DESIGN.md), shown only where the server serves it.
import FacilityRecordsDesk from './FacilityRecordsDesk';
// 2026-11-03: Before the visit (T0, read-only, dark behind MANIFEST_PREVISIT).
import PrevisitDesk from './PrevisitDesk';
import { facilityRecordsApi } from './facilityRecordsApi';
// 2026-10-27 (ruling, Isaiah Cotto): "Priority for the provider", set by the VA before the visit (OfficePriority.tsx).
import { OfficePriorityProvider, OfficePriorityToggle } from './OfficePriority';
import { AddBackControl, ClosedNotSeenNote, CloseNotSeen, ReviewLineFilter, SeenFact, applyLineFilter, type LineFilter } from './ManifestRounds';
import { advocateApi, Scope } from '../advocate/advocateApi';
import { StateBlock as TideState } from '../../components/tide';
import { MfSkeleton, MfStateBlock, Refusal, UNREACHED, failureFor } from './mfStates';
// The shared workspace bridge first (Tide phase 2), then this workspace's own sheet.
import '../../styles/tide/workspace.css';
import './manifest.css';
import { resubmittalsApi } from './resubmittalsApi';
import { verificationApi } from './verificationApi';
import { SendBackAction } from '../../components/sendBack/SendBackDialog';
import { sendBackPath } from '../../components/sendBack/sendBackApi';
import { floridaToday } from './floridaDay';
import { StatusPaletteContext, ToneChip } from './board/chips';
import MfBoard, { useClientSort, useOpenParam, type BoardColumn, type BoardDetailSection } from './board/MfBoard';
import MfLegend, { type MfLegendItem } from './board/MfLegend';
import { ResidentName, residentLabel, residentName } from './board/residents';
// 2026-10-28: a resident eMerge holds who is not on the building's census, added to a day's TWL Q.
import FindInEmerge, { DOES_NOT_MOVE, type FoundResident } from './FindInEmerge';
import { FindResidentEntry } from '../../components/residentDirectory';
import './manifests.board.css';

const BASE = '/portal/manifest';

const OFFLINE: Refusal = {
  title: 'eMerge could not be reached',
  body: 'Nothing was recorded. Check the connection and try again — no decision is lost by retrying.',
};

/**
 * Classify a read that did not succeed. A named gate keeps its own words; then
 * the status decides (2026-10 fix: a 500 or a dropped connection used to read
 * "That did not work ... eMerge refused the request"): unreached or 5xx is a
 * failure, 401/403 is a refusal the screen cannot name, anything else is the
 * server's own sentence.
 */
function refusalFor(code?: string | null, status?: number, message?: string): Refusal {
  if (code === 'EMERGE_PHI_ACCESS_DENIED') {
    return {
      title: 'You are not on the operator list',
      body: 'You hold the reviewer role, but the manifest names residents and this tenant keeps a separate list of who may see resident detail. Ask an administrator to add you.',
    };
  }
  if (code === 'DENY_NO_CAPABILITY' || code === 'FORBIDDEN') {
    return {
      title: 'This is not yours to review',
      body: 'Manifest review is granted by a ratified role. If you should hold it, an administrator can grant MANIFEST_REVIEWER.',
    };
  }
  const failed = failureFor({ status, code });
  if (failed) return failed;
  if (status === 401 || status === 403) {
    return {
      title: 'That did not work',
      body: 'eMerge refused the request and did not say why in a way this screen understands. Nothing was recorded.',
    };
  }
  return { title: 'Not loaded', body: message || 'eMerge answered without what this screen needs. Nothing was recorded.', failure: true, status };
}

const shortDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : iso;
};

const Skeleton = MfSkeleton;
const StateBlock = MfStateBlock;

const SORTERS: Record<string, (r: WorkableRow) => string | number | null> = {
  building: (r) => r.facility_name,
  last: (r) => r.last_visit,
  next: (r) => r.next_visit,
  provider: (r) => r.provider,
  progress: (r) => PROGRESS_ORDER.indexOf(progressKey(r)),
  twl: (r) => r.twl_confirmed,
};

/** A table row is a building: one row each, keyed by its id. */
const rowKey = (row: WorkableRow): string => row.key || row.facility_id;

/**
 * Start a building's review: the visit date, and the reviewer's word that the
 * visit took place. Nothing is created until the button is pressed, and this
 * form enforces both rules itself: the date may not be in the future (the
 * Florida day, as on the server) and the confirmation is required. The API
 * refuses either with VISIT_DATE_IN_FUTURE / VISIT_NOT_CONFIRMED as well.
 * Used in a row's docked panel and under "Find another building".
 */
function StartReview({ facilityName, busy, refusal, onStart }: {
  facilityName: string;
  busy: boolean;
  refusal: Refusal | null;
  onStart: (visitDate: string) => void;
}) {
  const [visitDate, setVisitDate] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const today = floridaToday();
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(visitDate) && visitDate <= today;
  return (
    <form className="mf-form mf-start" aria-label={`Start the review for ${facilityName}`} onSubmit={(event) => {
      event.preventDefault();
      if (validDate && confirmed && !busy) onStart(visitDate);
    }}>
      <label><span>Visit date</span><input type="date" required max={today} value={visitDate} disabled={busy} onChange={(event) => { setVisitDate(event.target.value); setConfirmed(false); }} /></label>
      <label><input type="checkbox" style={{ width: 'auto' }} checked={confirmed} disabled={!validDate || busy} onChange={(event) => setConfirmed(event.target.checked)} /> I confirm this visit took place on the selected date</label>
      <div className="mf-form-acts"><button type="submit" className="board-btn board-btn--primary" disabled={!validDate || !confirmed || busy}>Start the review</button></div>
      {refusal && <StateBlock refusal={refusal} />}
    </form>
  );
}

/**
 * "Find another building": the buildings that are NOT in the table (not Active,
 * no open review, no recent plan), found by name. It draws nothing until two
 * characters are typed and never more than ten rows, so it cannot become the
 * 3,688-row directory again (Isaiah Cotto, 2026-10-12).
 */
const FIND_MIN = 2;
function FindAnotherBuilding({ onStart, busyKey, refusal }: {
  onStart: (facilityId: string, visitDate: string) => void;
  busyKey: string | null;
  refusal: { facilityId: string; reason: Refusal } | null;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<OtherBuilding[]>([]);
  const [failed, setFailed] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  const typed = query.trim();
  const searching = typed.length >= FIND_MIN;

  useEffect(() => {
    if (!searching) { setFound([]); setFailed(false); return undefined; }
    let live = true;
    const timer = setTimeout(async () => {
      try {
        const r = await manifestApi.searchOthers(typed);
        if (!live) return;
        if (r.success && r.data) { setFound(r.data.items.slice(0, 10)); setFailed(false); } else { setFound([]); setFailed(true); }
      } catch { if (live) { setFound([]); setFailed(true); } }
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [typed, searching]);

  return (
    <section className="mf-find" aria-label="Find another building">
      <button type="button" className="board-btn" aria-expanded={open} aria-controls="mf-find-body" onClick={() => setOpen((o) => !o)}>
        Find another building
      </button>
      {open && (
        <div id="mf-find-body" className="mf-card mf-find-body">
          <div className="mf-card-body">
            <p className="mf-quiet">These buildings are not in the table: none is Active, has an open review or has a recent visit plan. Search by name to start a review in one.</p>
            <input type="search" className="mf-filter" aria-label="Search other buildings" placeholder="Building name" value={query}
              onChange={(event) => { setQuery(event.target.value); setChosen(null); }} />
            {!searching && <p className="mf-quiet" role="status">Type at least {FIND_MIN} letters of the building&apos;s name.</p>}
            {searching && failed && <p role="alert">The search did not complete. Try again.</p>}
            {searching && !failed && found.length === 0 && <p role="status">No other building matches &quot;{typed}&quot;.</p>}
            {searching && found.length > 0 && (
              <ul className="mf-buildings" aria-label="Other buildings">
                {found.map((b) => (
                  <li key={b.facility_id}>
                    <div className="mf-building">
                      <strong>{b.facility_name}</strong>
                      {(b.city || b.state) && <span className="mf-meta">{[b.city, b.state].filter(Boolean).join(', ')}</span>}
                      <span className="mf-progress">
                        <button type="button" className="board-btn" aria-expanded={chosen === b.facility_id} onClick={() => setChosen(chosen === b.facility_id ? null : b.facility_id)}>
                          Start a review for {b.facility_name}
                        </button>
                      </span>
                      {chosen === b.facility_id && (
                        <StartReview facilityName={b.facility_name} busy={busyKey !== null}
                          refusal={refusal && refusal.facilityId === b.facility_id ? refusal.reason : null}
                          onStart={(date) => onStart(b.facility_id, date)} />
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * Name the provider who made this visit, when eMerge's visit record named
 * nobody. The provider is who a send-back reaches; without one it refuses.
 */
function NameProvider({ reviewId, onNamed }: { reviewId: string; onNamed: (name: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const [choices, setChoices] = useState<ProviderChoice[] | null>(null);
  const [chosen, setChosen] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setOpen(true); setErr(null);
    try {
      const r = await manifestApi.providers();
      if (r && r.success && r.data) setChoices(r.data.items);
      else setErr((r && (r.error || r.message)) || 'The providers could not be loaded.');
    } catch { setErr('The providers could not be loaded.'); }
  };
  const save = async () => {
    if (!chosen) return;
    setBusy(true); setErr(null);
    try {
      const r = await manifestApi.setProvider(reviewId, chosen);
      if (r && r.success && r.data) { setOpen(false); onNamed(r.data.provider); }
      else setErr((r && (r.error || r.message)) || 'The provider was not recorded.');
    } catch { setErr('The provider was not recorded.'); } finally { setBusy(false); }
  };
  return (
    <div className="mf-note warn" role="note">
      <span>
        <strong>No provider is named for this visit.</strong>{' '}
        eMerge&apos;s visit record does not say who made it, so a resident cannot be sent back to the provider until you name them.{' '}
        {!open && <button type="button" className="mf-go ghost" onClick={start}>Name the provider</button>}
        {open && (
          <>
            <select aria-label="Provider who made this visit" value={chosen} onChange={(e) => setChosen(e.target.value)} disabled={!choices}>
              <option value="">{choices ? 'Choose the provider' : 'Loading…'}</option>
              {(choices || []).map((c) => <option key={c.id} value={c.id}>{c.name}{c.job_title ? ` (${c.job_title})` : ''}</option>)}
            </select>{' '}
            <button type="button" className="mf-go" disabled={!chosen || busy} onClick={save}>Record</button>{' '}
            <button type="button" className="mf-go ghost" onClick={() => setOpen(false)}>Cancel</button>
          </>
        )}
        {err && <span role="alert"> {err}</span>}
      </span>
    </div>
  );
}

type ProgressKey = 'notstarted' | 'undecided' | 'decided' | 'closed';

/** The legend's order, which is also the progress sort: least done first. */
const PROGRESS_ORDER: ProgressKey[] = ['notstarted', 'undecided', 'decided', 'closed'];

/** A building's progress is its current review's: none, some undecided, all decided, or closed. */
const progressKey = (row: WorkableRow): ProgressKey =>
  // A visit eMerge recorded with no review outranks an older closed review; an open review is still the work in hand.
  row.review_id === null || (row.pending_visit !== null && row.review_state !== 'OPEN') ? 'notstarted'
    : row.review_state === 'COMPLETED' ? 'closed'
      : row.undecided > 0 ? 'undecided' : 'decided';

/**
 * How far a building's manifest is. In the table a row says its own words
 * ("3 undecided"); in the legend a state is drawn with no row's count in it
 * (`row` omitted), so the legend groups rows by state and not by count text.
 */
function ProgressChip({ kind, row }: { kind: ProgressKey; row?: WorkableRow }) {
  if (kind === 'closed') return <ToneChip tone="quiet">{row?.reviewed_by ? `Closed by ${row.reviewed_by}` : 'Closed'}</ToneChip>;
  if (kind === 'notstarted') return <ToneChip tone="quiet">{row?.pending_visit ? `Visit ${shortDate(row.pending_visit.visit_date)} — not started` : 'Not started'}</ToneChip>;
  if (kind === 'undecided') return <ToneChip tone="attn">{row ? `${row.undecided} undecided` : 'Undecided residents'}</ToneChip>;
  return <ToneChip tone="ok">Complete</ToneChip>;
}

const PROGRESS_MEANS: Record<ProgressKey, { label: string; meaning: string }> = {
  notstarted: { label: 'Not started', meaning: 'Nobody has opened a review for this building. Open the row, then press Start the review.' },
  undecided: { label: 'Undecided residents', meaning: 'The review is open and some residents have no decision yet. The row says how many.' },
  decided: { label: 'Complete', meaning: 'Every resident has a decision. The review can be closed.' },
  closed: { label: 'Closed', meaning: 'The review was closed; its decisions are kept as recorded. Earlier closed reviews stay reachable from the building.' },
};

/**
 * One resident, with the decision on them.
 *
 * `busy` is per-line rather than per-screen: a reviewer working down a
 * manifest should not have the whole list freeze because one row is saving.
 */
function PersonRow({
  line, dispositions, canIntake, reviewOpen, busy, onDecide, onIntake, canSendBack = false, onSentBack,
  rounds, facilityId, onAddBack,
}: {
  line: ResidentLine;
  /** 2026-10-27: the provider's round against this review, and the building (for "add back"). */
  rounds?: ReviewDetail['rounds'];
  facilityId?: string;
  onAddBack?: (msg: string) => void;
  dispositions: Disposition[];
  canIntake: boolean;
  reviewOpen: boolean;
  busy: boolean;
  onDecide: (d: Disposition) => void;
  onIntake: () => void;
  /** The server's `can_send_back`: the caller may return a line they decided to its provider, signed. */
  canSendBack?: boolean;
  onSentBack?: (toName: string) => void;
}) {
  const decided = line.disposition !== null;
  const confirmed = line.disposition === 'TWL_CONFIRMED';
  const intake = line.intake;
  const notNew = Boolean(line.episode && line.episode.verdict !== 'NEW_LEAD_OK');
  // 2026-10-26: a PA already closed this resident DNQ / NITWL / C-NITWL after this visit (no intake yet): not a new lead.
  const worked = !intake && line.already_worked ? line.already_worked : null;
  const held = intake && intake.readiness_state !== 'READY' && intake.reasons.length > 0;

  return (
    <li>
      <div className={`mf-person${decided ? ' decided' : ''}`}>
        <div>
          <div className="mf-name"><ResidentName p={line} /></div>
          <div className="mf-pfacts">
            {line.room && <span>Room {line.room}</span>}
            {line.date_of_birth && <span>DOB {shortDate(line.date_of_birth)}</span>}
            {line.counselear_id && <span>CE {line.counselear_id}</span>}
            {!line.patient_id && <span className="mf-tag stop">Identity unresolved</span>}
            {line.source_tags.length > 0 && (
              <span className="mf-tags">
                {line.source_tags.map((t) => <span key={t} className="mf-tag quiet" title="From CounselEAR. Context for your decision, not a verdict.">{t}</span>)}
              </span>
            )}
            {line.has_open_case && <span className="mf-tag attn" title="A Patient Advocate case is already open for this resident.">Open case</span>}
            <SeenFact line={line} rounds={rounds} />
          </div>
          {notNew && <div className="mf-notes mf-episode" role="note"><strong>{line.episode!.line}.</strong> Not a new lead. If the provider saw them again for a replacement, record it on Replacements.</div>}
          {!notNew && worked && <div className="mf-notes mf-episode" role="note"><strong>{worked.line}.</strong> {worked.message}</div>}
          {line.alerts && <div className="mf-notes"><strong>Alert:</strong> {line.alerts}</div>}
          {line.notes && <div className="mf-notes">{line.notes}</div>}
          {decided && line.decided_by && (
            <div className="mf-notes"><span className="mf-quiet">{DISPOSITION_LABEL[line.disposition!]} · {line.decided_by}</span></div>
          )}
          <OfficePriorityToggle patientId={line.patient_id} name={residentLabel(line)} />
          {facilityId && (
            <AddBackControl line={line} facilityId={facilityId} rounds={rounds} name={residentLabel(line)} onChanged={(m) => onAddBack?.(m)} />
          )}
        </div>

        <div className="mf-decide" role="group" aria-label={`Decision for ${residentLabel(line)}`}>
          {dispositions.map((d) => (
            <button
              key={d}
              type="button"
              className={DISPOSITION_TONE[d]}
              aria-pressed={line.disposition === d}
              disabled={busy || !reviewOpen || ((notNew || Boolean(worked)) && d === 'TWL_CONFIRMED')}
              title={notNew && d === 'TWL_CONFIRMED' ? line.episode!.message || DISPOSITION_MEANS[d]
                : worked && d === 'TWL_CONFIRMED' ? worked.message : DISPOSITION_MEANS[d]}
              onClick={() => onDecide(d)}
            >
              {DISPOSITION_LABEL[d]}
            </button>
          ))}
        </div>

        {/* A decided line can be sent back for a retest. Undecided has no holder; the preview says so and the server decides again. */}
        {canSendBack && decided && (
          <div className="mf-intake">
            <SendBackAction
              path={sendBackPath.manifest(line.id)} recordLabel={residentLabel(line)} className="mf-go ghost" label="Send back to provider…"
              onClosed={(done) => { if (done) onSentBack?.(done.to.name); }}
            />
          </div>
        )}

        {confirmed && (
          <div className="mf-intake">
            {!intake && canIntake && (
              <button type="button" className="mf-go" disabled={busy || !line.patient_id || notNew || Boolean(worked)} title={notNew ? line.episode!.message || undefined : worked ? worked.message : undefined} onClick={onIntake}>
                Send to a Patient Advocate
              </button>
            )}
            {!intake && canIntake && !line.patient_id && (
              <span className="mf-quiet">eMerge cannot resolve who this is, so no case can be opened.</span>
            )}
            {!intake && !canIntake && (
              <span className="mf-quiet">Recorded. Creating the lead needs the intake capability, which you do not hold.</span>
            )}
            {intake && !held && <span className="mf-tag go">Lead created · ready for distribution</span>}
            {intake && held && (
              <div className="mf-held">
                <strong>Lead created, held out of distribution:</strong>
                <ul>{intake.reasons.map((r) => <li key={r}>{reasonWords(r)}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * The summary a recorded tally day leaves on the screen. Residents a PA already
 * closed DNQ / NITWL / C-NITWL after the visit (ALREADY_WORKED, 2026-10-26) are
 * named one by one with the reason, because each is a lead that was NOT opened
 * and the reviewer should not go looking for it.
 */
function twlDaySummary(r: TwlDayResult, where: string, names: Record<string, string> = {}): string {
  const worked = (r.results || []).filter((x) => x.outcome === 'ALREADY_WORKED');
  const workedCount = r.alreadyWorked ?? worked.length;
  const kept = r.providerMarksKept ?? 0;
  return `${where}: ${r.recorded} recorded · ${r.leadsCreated} ${r.leadsCreated === 1 ? 'lead' : 'leads'} created`
    + (r.ready ? `, ${r.ready} ready for distribution` : '')
    + (r.held ? `, ${r.held} held pending evidence` : '')
    + (r.notLeads ? `, ${r.notLeads} recorded but not a lead` : '')
    + (kept ? `, ${kept} kept as the provider marked ${kept === 1 ? 'it' : 'them'} at the building` : '')
    + (workedCount ? `, ${workedCount} already worked by a PA (no new lead)` : '')
    + (r.refused ? `, ${r.refused} refused` : '') + '.'
    + worked.map((x) => ` ${names[x.patientId] || 'A resident'}: ${x.message || x.why || 'A PA already closed this resident; no new lead was opened.'}`).join('')
    // 2026-10-28: the close the tally attempted, when it did not happen (it was swallowed before).
    + (r.complete_refused
      ? (r.complete_refused.code === 'REVIEW_NOT_OPEN'
        ? " This day's review was already closed; the resident was added to it."
        : ` The day's review stays open: ${r.complete_refused.message}`)
      : '');
}

/**
 * Record a day's TWL Q.
 *
 * Isaiah Cotto, 2026-09-28: "The TWLQ list is the tally of that days
 * manifests qualified company wide." Gwynn writes that tally at the end of
 * the day, and until now there was nowhere to put it: the Waiting list only
 * offers buildings eMerge already knew were visited, which on 2026-09-28 was
 * 7 of the 42 building-visits the tally actually recorded.
 *
 * So this names the building itself. Then it searches that building's census
 * rather than making her walk a hundred rows to find five — her tally is
 * three to seventeen names, and it is already written down.
 *
 * Nothing is sent until she presses Record. A half-entered day is worse than
 * an unentered one, because she cannot see which half landed.
 */
export function TwlDayForm({ words, onRecorded, onClose, prefill }: {
  words: string[];
  /** `names`: patientId -> the resident's label, for the lines the summary names (ALREADY_WORKED). */
  onRecorded: (r: TwlDayResult, facility: string, names?: Record<string, string>) => void;
  onClose: () => void;
  /**
   * 2026-10-28: opened from a review's docked panel -- that review's building, day and lines, so the form starts at
   * "who". The server opens nothing new: recording a day finds the review for that building and day.
   */
  prefill?: { facility: FacilityOption; visitDate: string; provider?: string | null; lines: ResidentLine[] };
}) {
  const [step, setStep] = useState<'where' | 'who'>(prefill ? 'who' : 'where');
  const [q, setQ] = useState('');
  const [options, setOptions] = useState<FacilityOption[]>([]);
  const [facility, setFacility] = useState<FacilityOption | null>(prefill ? prefill.facility : null);
  const [visitDate, setVisitDate] = useState(prefill ? prefill.visitDate : '');
  const [provider, setProvider] = useState(prefill && prefill.provider ? prefill.provider : '');
  const [lines, setLines] = useState<ResidentLine[]>(prefill ? prefill.lines : []);
  const [find, setFind] = useState('');
  const [picks, setPicks] = useState<Record<string, string>>({});
  // 2026-10-28: residents eMerge holds who are not on the census, found and added to this day (FindInEmerge).
  const [found, setFound] = useState<FoundResident[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Today on the business calendar (America/New_York), never toISOString and
  // never the browser's own zone: the API judges "future" by the same day.
  useEffect(() => { if (!visitDate) setVisitDate(floridaToday()); }, [visitDate]);

  useEffect(() => {
    if (q.trim().length < 2) { setOptions([]); return undefined; }
    let live = true;
    const t = window.setTimeout(async () => {
      try {
        const r = await manifestApi.facilities(q.trim());
        if (live && r.success && r.data) setOptions(r.data.items);
      } catch { /* the picker simply offers nothing */ }
    }, 250);
    return () => { live = false; window.clearTimeout(t); };
  }, [q]);

  const openDay = async () => {
    if (!facility || !visitDate) return;
    setBusy(true); setErr(null);
    try {
      // Recording a tally day is the reviewer saying the visit happened.
      const r = await manifestApi.open(facility.id, visitDate, { confirmed: true });
      if (!r.success || !r.data) { setErr(r.error || 'That day could not be opened.'); return; }
      setLines(r.data.residents);
      setStep('who');
    } catch { setErr('Could not reach the server.'); } finally { setBusy(false); }
  };

  const record = async () => {
    if (!facility) return;
    const residents = Object.entries(picks).map(([patient_id, outcome]) => ({ patient_id, outcome }));
    if (!residents.length) { setErr('Nobody is on this day yet.'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await manifestApi.recordDay({
        facility_id: facility.id, visit_date: visitDate,
        provider: provider.trim() || null, residents,
        // From a review's docked panel the review stays open: she closes it there herself.
        ...(prefill ? { complete: false } : {}),
      });
      if (!r.success || !r.data) { setErr(r.error || 'That day was not recorded.'); return; }
      const names: Record<string, string> = {};
      for (const l of lines) if (l.patient_id) names[l.patient_id] = residentLabel(l);
      for (const f of found) names[f.patient_id] = f.label;
      onRecorded(r.data, facility.name, names);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };

  const shown = lines.filter((l) => {
    if (!l.patient_id) return false;
    const t = find.trim().toLowerCase();
    if (!t) return Boolean(picks[l.patient_id]);
    return (residentName(l) || '').toLowerCase().includes(t) || String(l.room || '').toLowerCase().includes(t);
  });
  const chosen = Object.keys(picks).length;
  const taken = new Set<string>([
    ...lines.map((l) => String(l.patient_id || '').toLowerCase()).filter(Boolean),
    ...found.map((f) => f.patient_id.toLowerCase()),
  ]);
  const wordButtons = (patientId: string, label: string) => (
    <div className="mf-decide" role="group" aria-label={`What the tally says for ${label}`}>
      {words.map((w) => (
        <button key={w} type="button"
          className={picks[patientId] === w ? 'go' : 'quiet'}
          aria-pressed={picks[patientId] === w}
          onClick={() => setPicks((p) => {
            const next = { ...p };
            if (next[patientId] === w) delete next[patientId];
            else next[patientId] = w;
            return next;
          })}>
          {w}
        </button>
      ))}
    </div>
  );

  return (
    <section className="mf-card mf-twlday" aria-label="Record a day's TWL Q">
      <div className="mf-card-head">
        <h2>Record a day&rsquo;s TWL Q</h2>
        <button type="button" className="mf-go ghost" onClick={onClose}>Close</button>
      </div>

      {step === 'where' && (
        <div className="mf-form">
          <label><span>Building</span>
            {facility ? (
              <span className="mf-picked">
                {facility.name}
                <button type="button" className="mf-link" onClick={() => { setFacility(null); setQ(''); }}>Change</button>
              </span>
            ) : (
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Start typing the building's name" autoFocus />
            )}
          </label>
          {!facility && options.length > 0 && (
            <ul className="mf-options">
              {options.map((o) => (
                <li key={o.id}>
                  <button type="button" onClick={() => { setFacility(o); setOptions([]); }}>
                    {o.name}{o.city ? <span className="mf-quiet"> · {o.city}{o.state ? `, ${o.state}` : ''}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <label><span>Day of the visit</span>
            <input type="date" value={visitDate} max={floridaToday()} onChange={(e) => setVisitDate(e.target.value)} />
          </label>
          <label><span>Provider</span>
            <input value={provider} onChange={(e) => setProvider(e.target.value)} maxLength={120}
              placeholder="As the tally names them" />
          </label>
          {err && <p className="mf-note stop" role="alert">{err}</p>}
          <div className="mf-form-acts">
            <button type="button" className="mf-go" disabled={busy || !facility || !visitDate} onClick={openDay}>
              {busy ? 'Opening…' : 'Open the day'}
            </button>
          </div>
        </div>
      )}

      {step === 'who' && (
        <div className="mf-form">
          <p className="mf-sub">
            {facility?.name} · {shortDate(visitDate)}{provider ? ` · ${provider}` : ''} ·{' '}
            {lines.length} {lines.length === 1 ? 'resident' : 'residents'} on the census
          </p>
          <label><span>Find a resident</span>
            <input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Name or room" autoFocus />
          </label>

          {find.trim() && shown.length === 0 && (
            <TideState kind="empty" reason="filtered" message={<>Nobody on this building&rsquo;s census matches that.</>} />
          )}
          {!find.trim() && chosen === 0 && (
            <p className="mf-empty">Search for each resident on your tally and give them the word you wrote down.</p>
          )}

          <ul className="mf-picks">
            {shown.map((l) => (
              <li key={l.id}>
                <div className="mf-name"><ResidentName p={l} />{l.room ? <span className="mf-quiet"> · Room {l.room}</span> : null}</div>
                {wordButtons(l.patient_id!, residentLabel(l))}
              </li>
            ))}
          </ul>

          {facility && (
            <FindInEmerge
              facility={facility}
              visitDateLabel={shortDate(visitDate)}
              taken={taken}
              onPick={(r) => setFound((cur) => (cur.some((x) => x.patient_id === r.patient_id) ? cur : [...cur, r]))}
            />
          )}
          {found.length > 0 && (
            <section aria-label="Added from eMerge">
              <p className="mf-sub">Added from eMerge, not on {facility?.name}&rsquo;s census</p>
              <ul className="mf-picks">
                {found.map((f) => (
                  <li key={f.patient_id}>
                    <div>
                      <div className="mf-name"><strong>{f.label}</strong></div>
                      <p className="mf-quiet">{f.where}</p>
                    </div>
                    {wordButtons(f.patient_id, f.label)}
                    <button type="button" className="mf-linkbtn" onClick={() => {
                      setFound((cur) => cur.filter((x) => x.patient_id !== f.patient_id));
                      setPicks((p) => { const next = { ...p }; delete next[f.patient_id]; return next; });
                    }}>Remove from this day</button>
                  </li>
                ))}
              </ul>
              <p className="mf-note" role="note">{DOES_NOT_MOVE}</p>
            </section>
          )}

          {err && <p className="mf-note stop" role="alert">{err}</p>}
          <div className="mf-form-acts">
            <button type="button" className="mf-go" disabled={busy || chosen === 0} onClick={record}>
              {busy ? 'Recording…' : `Record the day · ${chosen}`}
            </button>
            {!prefill && <button type="button" className="mf-go ghost" disabled={busy} onClick={() => setStep('where')}>Back</button>}
          </div>
        </div>
      )}
    </section>
  );
}

export default function ManifestShell() {
  const [params, setParams] = useSearchParams();
  // The open review lives in the URL (?review=<id>), so a link or a refresh lands on it.
  const [reviewId, setReview] = useOpenParam('review', { push: true });
  // The desk's views (2026-09-30), each its own link: ?view=leads, ?view=coverage, ?view=replacements.
  // And Unassigned, moved here from the Patient Advocate workspace the same day: ?view=unassigned.
  // 'verification' (2026-10-02): leads a VA checks benefits for, after Resubmittals.
  // 'resubmittals' (2026-10-01): residents a PA sent back to the provider, after Replacements.
  // 'no-building' (2026-10-13): residents created from CounselEAR with no confirmed building, which no building's list can show.
  // 'arrivals' (2026-10-14): CounselEAR arrival reviews, which the manifest reviewer rules (ruling, Isaiah Cotto, 2026-10-13).
  // 'records' (2026-10-27): Facility records, the visit records each building receives (FacilityRecordsDesk).
  // 'previsit' (2026-11-03): Before the visit, the main function of Manifest Review (ruling, Isaiah Cotto); shown only when the index says previsit_enabled.
  const DESK_VIEWS = ['previsit', 'leads', 'coverage', 'replacements', 'unassigned', 'resubmittals', 'verification', 'no-building', 'arrivals', 'records'] as const;
  type DeskView = typeof DESK_VIEWS[number];
  const rawView = params.get('view');
  const deskView = (DESK_VIEWS as readonly string[]).includes(rawView || '') ? rawView as DeskView : null;
  const goToView = (v: DeskView | null, replace = false) => {
    const next = new URLSearchParams();
    if (v) next.set('view', v);
    setParams(next, { replace });
  };

  /*
   * Unassigned is shown to whoever the server lets place PA work
   * (ADVOCACY_TEAM_MANAGE, read from the advocate workspace's own scope),
   * not to every reviewer: a reviewer who is not a PA manager would get a
   * tab whose every request is refused. The server decides again on every call.
   */
  const [paScope, setPaScope] = useState<Scope | null>(null);
  const [paScopeKnown, setPaScopeKnown] = useState(false);
  const loadPaScope = useCallback(async () => {
    try {
      const r = await advocateApi.scope();
      setPaScope(r.success && r.data ? r.data : null);
    } catch { setPaScope(null); } finally { setPaScopeKnown(true); }
  }, []);
  useEffect(() => { loadPaScope(); }, [loadPaScope]);
  const placesPaWork = Boolean(paScope?.manager);
  // Who may rule on CounselEAR arrival reviews: read from the identity scope on its own,
  // never through the Patient Advocate scope the manifest reviewer does not hold.
  const [identityScope, setIdentityScope] = useState<IdentityScope | null>(null);
  useEffect(() => {
    let live = true;
    residentsApi.scope().then((r) => { if (live) setIdentityScope(r.success && r.data ? r.data : null); }).catch(() => { if (live) setIdentityScope(null); });
    return () => { live = false; };
  }, []);
  const rulesArrivals = reviewsArrivals(identityScope);
  // A hand-typed ?view=unassigned from someone who cannot place work lands on the manifests.
  useEffect(() => {
    if (deskView === 'unassigned' && paScopeKnown && !placesPaWork) goToView(null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deskView, paScopeKnown, placesPaWork]);

  // The send-back flag lives on the resubmittals read (design §4.1); a failed read is "no flag", never a reason to offer the act.
  const [canSendBack, setCanSendBack] = useState(false);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await resubmittalsApi.list();
        if (live) setCanSendBack(Boolean(r.success && r.data && r.data.can_send_back));
      } catch { if (live) setCanSendBack(false); }
    })();
    return () => { live = false; };
  }, []);

  const [index, setIndex] = useState<ManifestIndex | null>(null);
  const [indexLoading, setIndexLoading] = useState(true);
  const [indexRefusal, setIndexRefusal] = useState<Refusal | null>(null);

  const [detail, setDetail] = useState<ReviewDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRefusal, setDetailRefusal] = useState<Refusal | null>(null);

  const [busyLine, setBusyLine] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [legendKey, setLegendKey] = useState<string | null>(null);
  // A row with no review yet has no id to put in the URL: it opens a panel that offers to start one.
  const [unstartedKey, setUnstartedKey] = useState<string | null>(null);
  const [showTwlDay, setShowTwlDay] = useState(false);
  // 2026-10-28: the review whose docked panel has "Record this day's TWL Q" open (null: none).
  const [dockTally, setDockTally] = useState<string | null>(null);
  // 2026-10-27: Seen by provider / Not seen / Undecided above a review's residents (ManifestRounds.tsx); per review.
  const [lineFilter, setLineFilter] = useState<LineFilter>('all');
  useEffect(() => { setLineFilter('all'); }, [reviewId]);

  const loadIndex = useCallback(async () => {
    setIndexLoading(true); setIndexRefusal(null);
    try {
      const r = await manifestApi.index();
      if (r.success && r.data) setIndex(r.data);
      else setIndexRefusal(refusalFor(r.code, r.status, r.message));
    } catch { setIndexRefusal(UNREACHED); } finally { setIndexLoading(false); }
  }, []);
  useEffect(() => { loadIndex(); }, [loadIndex]);

  /*
   * The number on the Needs verification tab (2026-10-22): OPEN requests in the reader's buildings, the list's own
   * scope (GET /verifications/count). Asked once, and only after the manifests index loaded: the index and the
   * count sit behind the same gate (the PHI operator list and MANIFEST_REVIEW), so somebody the index refuses is
   * never sent a second request that would only be refused and logged as a security denial. The desk reports its
   * own total each time it loads, so an answer moves it too. A failed read shows no number, never a wrong one.
   */
  const reviewHeld = Boolean(index);
  /*
   * Facility records is offered only when the server serves it (2026-10-27): before its migrations the desk answers
   * 503 RECORDS_NOT_READY and the tab stays hidden; a refusal hides it too. Asked once, after the index loaded.
   */
  const [recordsReady, setRecordsReady] = useState(false);
  // Phase 0c: the latest provider close-out per building (its most recent visit day), from the same read.
  const [closedOut, setClosedOut] = useState<Record<string, { at: string; by_first_name: string; visit_date: string }>>({});
  const recordsAsked = React.useRef(false);
  useEffect(() => {
    if (!reviewHeld || recordsAsked.current) return undefined;
    recordsAsked.current = true;
    // The light read (security review item 8): the latest close-out per building, never the whole desk.
    facilityRecordsApi.closeouts().then((r) => {
      setRecordsReady(Boolean(r.success && r.data && r.data.ready));
      const latest: Record<string, { at: string; by_first_name: string; visit_date: string }> = {};
      for (const c of (r.success && r.data && Array.isArray(r.data.closeouts) ? r.data.closeouts : [])) {
        const cur = latest[c.facility_id];
        if (!cur || c.visit_date > cur.visit_date) latest[c.facility_id] = { at: c.at, by_first_name: c.by_first_name, visit_date: c.visit_date };
      }
      setClosedOut(latest);
    }).catch(() => setRecordsReady(false));
    return undefined;
  }, [reviewHeld]);
  const [verificationOpen, setVerificationOpen] = useState<number | null>(null);
  const countAsked = React.useRef(false);
  useEffect(() => {
    if (!reviewHeld || countAsked.current) return undefined;
    countAsked.current = true;
    // No unmount guard: the request is made once (StrictMode's second run finds countAsked set), so its answer must land.
    verificationApi.count()
      .then((r) => setVerificationOpen(r.success && r.data && typeof r.data.open === 'number' ? r.data.open : null))
      .catch(() => setVerificationOpen(null));
    return undefined;
  }, [reviewHeld]);

  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true); setDetailRefusal(null);
    try {
      const r = await manifestApi.detail(id);
      if (r.success && r.data) setDetail(r.data);
      else {
        setDetail(null);
        setDetailRefusal(r.code === 'NOT_FOUND'
          ? { title: 'No such review here', body: 'This review does not exist, or it belongs to a building outside your scope. Those are deliberately the same answer.' }
          : refusalFor(r.code, r.status, r.message));
      }
    } catch { setDetail(null); setDetailRefusal(UNREACHED); } finally { setDetailLoading(false); }
  }, []);

  useEffect(() => { setNotice(null); setProblem(null); }, [reviewId]);
  useEffect(() => { if (reviewId) loadDetail(reviewId); else setDetail(null); }, [reviewId, loadDetail]);

  const goToReview = (id: string | null) => { setReview(id); };

  /**
   * Start a building's review on a visit date the reviewer names and confirms.
   * Idempotent on the server by (building, visit date), so starting a day
   * already in progress joins that review rather than creating a second one.
   * A refusal is said where it was asked for: in that building's own form.
   */
  const [busyStartKey, setBusyStartKey] = useState<string | null>(null);
  const [startRefusal, setStartRefusal] = useState<{ facilityId: string; reason: Refusal } | null>(null);
  const startReview = async (facilityId: string, visitDate: string, anchor: { visitPlanId?: string | null; checkInId?: string | null } = {}) => {
    setProblem(null); setStartRefusal(null); setBusyStartKey(facilityId);
    try {
      // Every caller has the reviewer's confirmation (the form's checkbox) or an eMerge-recorded visit.
      const r = await manifestApi.open(facilityId, visitDate, { ...anchor, confirmed: true });
      if (r.success && r.data) {
        setDetail(r.data);
        setUnstartedKey(null);
        setLegendKey(null);
        goToReview(r.data.review.id);
        loadIndex();
      } else setStartRefusal({ facilityId, reason: refusalFor(r.code, r.status, r.message) });
    } catch { setStartRefusal({ facilityId, reason: UNREACHED }); } finally { setBusyStartKey(null); }
  };

  const decide = async (line: ResidentLine, d: Disposition) => {
    if (!detail) return;
    setBusyLine(line.id); setProblem(null);
    try {
      const r = await manifestApi.decide(detail.review.id, line.id, d);
      if (r.success) await loadDetail(detail.review.id);
      else setProblem(r.error || r.message || 'That decision was not recorded.');
    } catch { setProblem(OFFLINE.body); } finally { setBusyLine(null); }
  };

  const sendToAdvocate = async (line: ResidentLine) => {
    if (!detail) return;
    setBusyLine(line.id); setProblem(null);
    try {
      const r = await manifestApi.addToIntake(detail.review.id, line.id);
      if (r.success && r.data) {
        const out = r.data as IntakeResult;
        setNotice(out.readiness_state === 'READY'
          ? `${residentLabel(line)} is now a lead, ready for distribution.`
          : `${residentLabel(line)} is now a lead, held until ${out.reasons.map(reasonWords).join(' and ')}.`);
        await loadDetail(detail.review.id);
        loadIndex();
      } else { setProblem(r.error || r.message || 'The lead was not created.'); }
    } catch { setProblem(OFFLINE.body); } finally { setBusyLine(null); }
  };

  const closeMorning = async () => {
    if (!detail) return;
    setProblem(null);
    try {
      const r = await manifestApi.complete(detail.review.id);
      if (r.success) { setNotice('This manifest is closed.'); await loadDetail(detail.review.id); loadIndex(); }
      else setProblem(r.error || r.message || 'This review was not closed.');
    } catch { setProblem(OFFLINE.body); }
  };

  /** 2026-10-27: close with the residents the provider did not see as "Not seen at visit" (ManifestRounds.tsx). */
  const closeNotSeen = async (expected: number): Promise<boolean> => {
    if (!detail) return false;
    setProblem(null);
    try {
      const r = await manifestApi.complete(detail.review.id, { closeNotSeen: true, expectedNotSeen: expected });
      if (r.success) {
        const n = r.data && typeof r.data.closed_not_seen === 'number' ? r.data.closed_not_seen : expected;
        setNotice(`This manifest is closed. ${n} ${n === 1 ? 'resident' : 'residents'} not seen at the visit closed without a decision.`);
        await loadDetail(detail.review.id); loadIndex();
        return true;
      }
      setProblem(r.error || r.message || 'This review was not closed.');
      // A changed count: read the review again so the screen shows the new one.
      await loadDetail(detail.review.id);
      return true;
    } catch { setProblem(OFFLINE.body); return false; }
  };

  const dispositions = index?.dispositions ?? [];
  const canIntake = Boolean(index?.can.create_intake);
  // The table: one row per workable building (Isaiah Cotto, 2026-10-12), whatever its reviews.
  const workableRows = useMemo(() => index?.workable?.items ?? [], [index]);

  // The review in the URL, once its detail has loaded: which building it belongs to.
  const detailFor = detail && reviewId && detail.review.id === reviewId ? detail : null;
  const matches = (r: WorkableRow): boolean => Boolean(reviewId) && (
    r.review_id === reviewId
    || r.earlier_reviews.some((e) => e.id === reviewId)
    || Boolean(detailFor && r.facility_id === detailFor.review.facility_id)
  );
  // A building the index does not list (a hand-typed link, a review just started from "Find another building") still gets its row, so its panel can open.
  const listed = workableRows.some(matches);
  const synthetic: WorkableRow | null = detailFor && !listed ? {
    key: detailFor.review.facility_id,
    facility_id: detailFor.review.facility_id, facility_name: detailFor.review.facility_name,
    manifest_url: detailFor.review.manifest_url, link_state: detailFor.review.manifest_url ? 'AVAILABLE' : 'MISSING',
    last_visit: detailFor.review.visit_date, last_visit_source: 'EMERGE', next_visit: null, next_visit_source: null, pending_visit: null,
    provider: detailFor.review.provider,
    review_id: detailFor.review.id, review_state: detailFor.review.state, review_visit_date: detailFor.review.visit_date,
    review_manifest_url: detailFor.review.manifest_url,
    lines_total: detailFor.counts.lines, twl_confirmed: detailFor.counts.twl_confirmed, undecided: detailFor.counts.undecided,
    reviewed_by: detailFor.review.completed_by, reviewed_at: detailFor.review.completed_at, earlier_reviews: [],
  } : null;
  const baseRows: WorkableRow[] = [...(synthetic ? [synthetic] : []), ...workableRows];
  const shown = legendKey ? baseRows.filter((r) => progressKey(r) === legendKey) : baseRows;
  const sorted = useClientSort(shown, SORTERS, { sort: null });

  // The header's number is the buildings in the table; the sentence beside it sums the open reviews' undecided residents.
  const totals = {
    buildings: baseRows.length,
    undecided: baseRows.reduce((n, r) => n + (r.review_state === 'OPEN' ? r.undecided : 0), 0),
  };

  // Grouped by STATE, not by a row's own count text: one entry per state, each with its row count.
  const legendItems: MfLegendItem[] = PROGRESS_ORDER.flatMap((k) => {
    const count = baseRows.filter((r) => progressKey(r) === k).length;
    if (count === 0 && legendKey !== k) return [];
    return [{
      key: k, label: PROGRESS_MEANS[k].label, sample: <ProgressChip kind={k} />,
      meaning: PROGRESS_MEANS[k].meaning, count,
    }];
  });

  const openRowMatch = reviewId ? baseRows.find(matches) : undefined;
  const openKey = openRowMatch ? rowKey(openRowMatch) : (unstartedKey && baseRows.some((r) => rowKey(r) === unstartedKey) ? unstartedKey : null);

  const onOpenChange = (key: string | null) => {
    setProblem(null); setDetailRefusal(null); setStartRefusal(null);
    if (!key) { setUnstartedKey(null); goToReview(null); return; }
    const row = baseRows.find((r) => rowKey(r) === key);
    if (!row) return;
    if (row.review_id) { setUnstartedKey(null); goToReview(row.review_id); } else { setUnstartedKey(key); goToReview(null); }
  };

  /** A date column: eMerge's own date, or the Monday mirror's, said so (it proves no visit). */
  const dateCell = (iso: string | null, source: 'EMERGE' | 'MONDAY' | null) => (
    iso
      ? (
        <span>
          {shortDate(iso)}
          {source === 'MONDAY' && <span className="mf-meta mf-src" title="From the Monday scheduling mirror, shown because eMerge holds no date. It does not prove a visit.">Monday</span>}
        </span>
      )
      : <span className="board-na">none</span>
  );

  const columns: BoardColumn<WorkableRow>[] = [
    {
      key: 'manifest', label: 'Manifest', help: "The building's current manifest link, held by eMerge. A missing link is set by Scheduling.",
      render: (r) => (r.link_state === 'AVAILABLE' && r.manifest_url
        ? <a className="mf-link" href={r.manifest_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Open manifest</a>
        : <span className="board-na">{r.link_state === 'CLEARED' ? 'Link cleared' : 'Link missing'}</span>),
    },
    { key: 'last', label: 'Last visit', sort: 'last', help: "eMerge's latest completed plan, provider check-in or review date; the Monday mirror's date only when eMerge holds none, marked Monday.", render: (r) => dateCell(r.last_visit, r.last_visit_source) },
    { key: 'next', label: 'Next visit', sort: 'next', help: "The soonest planned visit from today on, from eMerge's plans; the Monday mirror's date only when eMerge holds none, marked Monday.", render: (r) => dateCell(r.next_visit, r.next_visit_source) },
    {
      key: 'provider', label: 'Provider', sort: 'provider', help: "Who made the visit on the building's review, else who is planned for the next visit, else Monday's assignee.",
      render: (r) => (r.provider ? <span className="board-clip" title={r.provider}>{r.provider}</span> : <span className="board-na">not named</span>),
    },
    { key: 'progress', label: 'Progress', sort: 'progress', help: "How far the building's review is: not started, how many residents are undecided, complete, or closed and by whom.", render: (r) => <ProgressChip kind={progressKey(r)} row={r} /> },
    { key: 'twl', label: 'TWL confirmed', numeric: true, sort: 'twl', help: 'Residents a reviewer confirmed as a TWL on the building\'s current review.', render: (r) => <span>{r.twl_confirmed}</span> },
  ];

  const identity = {
    label: 'Building',
    sort: 'building',
    help: 'A building a VA can work: Active, or with an open review, or planned from two weeks ago on. Open the row to review its residents, docked beside the table.',
    render: (r: WorkableRow) => {
      const co = closedOut[r.facility_id];
      return (
        <>
          <strong>{r.facility_name}</strong>
          {co && (
            <span className="mf-sub" data-testid={`closed-out-${r.facility_id}`}>
              {' '}Visit closed out {new Date(co.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })} by {co.by_first_name}
              {co.visit_date !== floridaToday() ? ` (${shortDate(co.visit_date)})` : ''}
            </span>
          )}
        </>
      );
    },
    name: (r: WorkableRow) => r.facility_name,
  };

  /**
   * Starting a review at a building: the one-click start for a visit eMerge
   * already recorded (with its plan or check-in), and the date form for any
   * other visit. Offered on every row, so a building that already has a review
   * can still get another. `collapsed`: the building has an open review, which
   * stays the first thing shown.
   */
  const startBlock = (row: WorkableRow, collapsed: boolean): React.ReactNode => {
    const pv = row.pending_visit;
    const form = (
      <StartReview key={row.facility_id} facilityName={row.facility_name} busy={busyStartKey !== null}
        refusal={startRefusal && startRefusal.facilityId === row.facility_id ? startRefusal.reason : null}
        onStart={(date) => startReview(row.facility_id, date)} />
    );
    return (
      <div className="mf-panel">
        {pv && (
          <div className="mf-acts">
            <p>eMerge recorded a visit on {shortDate(pv.visit_date)} that has no review yet.</p>
            <button type="button" className="board-btn board-btn--primary" disabled={busyStartKey !== null}
              onClick={() => startReview(row.facility_id, pv.visit_date, { visitPlanId: pv.visit_plan_id, checkInId: pv.check_in_id })}>
              Open the review for the {shortDate(pv.visit_date)} visit
            </button>
          </div>
        )}
        {collapsed ? (
          <details className="mf-another">
            <summary>Start a review for another visit</summary>
            {form}
          </details>
        ) : (
          <>
            <p>{pv ? 'Or choose another day:' : 'Choose the day of the visit and confirm it took place; nothing is created until you press the button.'}</p>
            {form}
          </>
        )}
      </div>
    );
  };

  const sections = (row: WorkableRow): BoardDetailSection[] => {
    const reviewing = Boolean(reviewId) && matches(row);
    if (!reviewing && !row.review_id) {
      return [{
        id: 'review', label: 'Review',
        render: () => (
          <div className="mf-panel">
            <p><ToneChip tone="quiet">Not started</ToneChip></p>
            <p>Nobody has opened a review for {row.facility_name}.</p>
            {row.link_state !== 'AVAILABLE' && (
              <p className="mf-quiet">eMerge holds no manifest link for this building. A Scheduling manager sets it on the Scheduling board.</p>
            )}
            {startBlock(row, false)}
          </div>
        ),
      }];
    }
    if (detailRefusal) {
      return [{ id: 'residents', label: 'Residents', render: () => <StateBlock refusal={detailRefusal} retry={reviewId ? () => loadDetail(reviewId) : undefined} /> }];
    }
    const d = detailFor;
    if (!d) return [{ id: 'residents', label: 'Residents', render: () => <Skeleton /> }];
    // Every review of this building but the one on screen: closing a morning never hides its history.
    const history: WorkableReviewRef[] = [
      ...(row.review_id ? [{ id: row.review_id, visit_date: row.review_visit_date || row.last_visit || '', state: row.review_state || 'OPEN' } as WorkableReviewRef] : []),
      ...row.earlier_reviews,
    ].filter((h) => h.id !== d.review.id);
    const out: BoardDetailSection[] = [{
      id: 'residents', label: 'Residents',
      render: () => (
        <div className="mf-panel">
          <p className="mf-quiet">
            {d.counts.lines} {d.counts.lines === 1 ? 'resident' : 'residents'}
            {d.counts.undecided > 0 ? ` · ${d.counts.undecided} undecided` : ''}
            {d.counts.closed_not_seen ? ` · ${d.counts.closed_not_seen} not seen at visit` : ''}
            {d.counts.withdrawn ? ` · ${d.counts.withdrawn} ${d.counts.withdrawn === 1 ? 'line' : 'lines'} withdrawn (not on this building's census)` : ''}
            {d.counts.twl_confirmed > 0 ? ` · ${d.counts.twl_confirmed} confirmed TWL · ${d.counts.intakes} sent` : ''}
            {d.rounds && d.rounds.ready && d.rounds.seen_lines > 0 ? ` · ${d.rounds.seen_lines} seen on the provider's round` : ''}
          </p>

          {!d.review.snapshot_imported && (
            <div className="mf-note warn">
              <span>
                <strong>These lines are eMerge&apos;s own residents for this building, not the workbook.</strong>{' '}
                The file itself has not been read into this review, so a resident the provider wrote onto the sheet
                who is not in eMerge will not appear below.
                {d.review.manifest_url && <> Open the manifest alongside this list: </>}
                {d.review.manifest_url && (
                  <a className="mf-link" href={d.review.manifest_url} target="_blank" rel="noopener noreferrer">the workbook</a>
                )}
              </span>
            </div>
          )}

          {d.review.provider_employee_id === null && (
            <NameProvider
              reviewId={d.review.id}
              onNamed={(name) => { setNotice(`${name || 'The provider'} is recorded as this visit's provider.`); loadDetail(d.review.id); loadIndex(); }}
            />
          )}

          {d.review.state === 'COMPLETED' && (
            <div className="mf-note">
              <span>
                <strong>Closed{d.review.completed_by ? ` by ${d.review.completed_by}` : ''}.</strong>{' '}
                Decisions are kept as they were recorded.
                <ClosedNotSeenNote detail={d} />
              </span>
            </div>
          )}

          {d.residents.length === 0 ? (
            <TideState kind="empty" reason="none" message="eMerge holds no confirmed residents for this building, so there is nothing to decide against the manifest." />
          ) : (
            <OfficePriorityProvider facilityId={d.review.facility_id}>
            <ReviewLineFilter lines={d.residents} rounds={d.rounds} value={lineFilter} onChange={setLineFilter} />
            {applyLineFilter(d.residents, lineFilter).length === 0 && (
              <p className="mf-quiet" role="status">No resident on this review matches that. <button type="button" className="mf-linkbtn" onClick={() => setLineFilter('all')}>Show all</button></p>
            )}
            <ul className="mf-rows">
              {applyLineFilter(d.residents, lineFilter).map((line) => (
                <PersonRow
                  key={line.id}
                  line={line}
                  dispositions={dispositions}
                  canIntake={canIntake}
                  reviewOpen={d.review.state === 'OPEN'}
                  busy={busyLine === line.id}
                  onDecide={(x) => decide(line, x)}
                  onIntake={() => sendToAdvocate(line)}
                  canSendBack={canSendBack}
                  onSentBack={(name) => { setNotice(`${residentLabel(line)} was sent back to ${name}.`); loadDetail(d.review.id); }}
                  rounds={d.rounds}
                  facilityId={d.review.facility_id}
                  onAddBack={(m) => { setNotice(m); loadDetail(d.review.id); }}
                />
              ))}
            </ul>
            </OfficePriorityProvider>
          )}
        </div>
      ),
    }];
    if (d.review.state === 'OPEN') {
      out.push({
        id: 'close', label: 'Close',
        render: () => (
          <div className="mf-panel">
            <div className="mf-acts">
              <button type="button" className="board-btn board-btn--primary" disabled={d.counts.undecided > 0} onClick={closeMorning}>
                Close this manifest
              </button>
            </div>
            {d.counts.undecided > 0 && (
              <p className="mf-quiet">
                {d.counts.undecided} {d.counts.undecided === 1 ? 'resident has' : 'residents have'} no decision yet.
                &quot;Needs review&quot; is a decision — it says you looked.
              </p>
            )}
            <CloseNotSeen detail={d} onClose={closeNotSeen} />
          </div>
        ),
      });
    }
    // 2026-10-28: the end-of-day tally for THIS review's building and day, from its docked panel, so a resident eMerge
    // holds who is not on the census can be found and recorded here (FindInEmerge). Only for somebody who may create
    // a lead, and only on an open review; the server decides both again.
    if (d.review.state === 'OPEN' && canIntake && index) {
      out.push({
        id: 'tally', label: 'Record TWL Q',
        render: () => (
          <div className="mf-panel">
            {dockTally === d.review.id ? (
              <TwlDayForm
                words={index.twl_outcomes || []}
                prefill={{
                  facility: { id: d.review.facility_id, name: d.review.facility_name, city: null, state: null },
                  visitDate: d.review.visit_date, provider: d.review.provider, lines: d.residents,
                }}
                onClose={() => setDockTally(null)}
                onRecorded={(r, where, names) => {
                  setDockTally(null);
                  setNotice(twlDaySummary(r, where, names));
                  loadDetail(d.review.id);
                  loadIndex();
                }}
              />
            ) : (
              <>
                <p>
                  Record the end-of-day TWL Q for {d.review.facility_name}, {shortDate(d.review.visit_date)}: the residents on
                  this review, and anyone eMerge holds who is not on this building&rsquo;s census.
                </p>
                <div className="mf-acts">
                  <button type="button" className="board-btn board-btn--primary" onClick={() => setDockTally(d.review.id)}>
                    Record this day&rsquo;s TWL Q…
                  </button>
                </div>
                <p className="mf-quiet">The review stays open; close it from Close when every resident has a decision.</p>
              </>
            )}
          </div>
        ),
      });
    }
    const startSection: BoardDetailSection = { id: 'start', label: 'Start a review', render: () => startBlock(row, row.review_state === 'OPEN') };
    // A closed review (or none) puts the start form first; an open one stays first and the start folds below it.
    if (row.review_state === 'COMPLETED') out.unshift(startSection); else out.push(startSection);
    if (history.length > 0) {
      out.push({
        id: 'history', label: 'Other reviews',
        render: () => (
          <div className="mf-panel">
            <p className="mf-quiet">Other reviews of {row.facility_name}. Pressing one opens it here.</p>
            <ul className="mf-history">
              {history.map((h) => (
                <li key={h.id}>
                  <button type="button" className="mf-go ghost" onClick={() => goToReview(h.id)}>
                    {shortDate(h.visit_date)} · {h.state === 'COMPLETED' ? 'Closed' : 'Open'}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ),
      });
    }
    return out;
  };

  let state: React.ReactNode = null;
  if (indexRefusal) state = <StateBlock refusal={indexRefusal} retry={loadIndex} />;
  else if (indexLoading && !index) state = <Skeleton />;
  else if (index && baseRows.length === 0) {
    state = <TideState kind="empty" reason="none" kindLabel="Nothing to work" message="No building is workable right now: none is Active, has an open review or has a visit plan from the last two weeks on. Find another building to start a review." />;
  }

  // A review that has no row to dock into (a refusal, or still loading) is said above the table.
  const rowless = !openRowMatch && !(unstartedKey && openKey === unstartedKey);

  /*
   * The desk's views, as the sidebar's links (Isaiah Cotto, 2026-10-30: one standard sidebar) in place of the bar's tab
   * strip. Each is the URL it always was (?view=leads, ?view=coverage, ...), so a link can be opened, shared and returned
   * to; the Manifests item drops ?view and ?review, as the button it replaces did.
   */
  const deskItem = (key: string, label: string, icon: SidebarItem['icon'], view: DeskView | null, count?: number | null, countLabel?: string): SidebarItem => ({
    key, label, icon, href: view ? `${BASE}?view=${view}` : BASE, active: deskView === view,
    badge: count !== undefined && count !== null ? { value: count, label: countLabel } : null,
  });
  const workItems: SidebarItem[] = [
    ...(index?.previsit_enabled ? [deskItem('previsit', 'Before the visit', CalendarClock, 'previsit')] : []),
    deskItem('manifests', 'Manifests', ClipboardList, null),
    deskItem('leads', 'Leads to place', UserPlus, 'leads'),
    ...(placesPaWork ? [deskItem('unassigned', 'Unassigned', UserSearch, 'unassigned', paScope?.counts.unassigned, `${paScope?.counts.unassigned?.toLocaleString('en-US')} unassigned`)] : []),
    deskItem('replacements', 'Replacements', Ear, 'replacements'),
    deskItem('resubmittals', 'Resubmittals', RotateCcw, 'resubmittals'),
    deskItem('verification', 'Needs verification', SearchCheck, 'verification', verificationOpen, `${verificationOpen?.toLocaleString('en-US')} needing verification`),
  ];
  const referenceItems: SidebarItem[] = [
    deskItem('coverage', 'Facility coverage', MapPinned, 'coverage'),
    deskItem('no-building', 'No building', Building2, 'no-building'),
    ...((recordsReady || deskView === 'records') ? [deskItem('records', 'Facility records', FolderOpen, 'records')] : []),
    ...(rulesArrivals ? [deskItem('arrivals', 'Arrival reviews', Users, 'arrivals')] : []),
  ];
  const sidebarGroups: SidebarGroup[] = [
    { key: 'work', label: 'Work', items: workItems },
    { key: 'reference', label: 'Reference', items: referenceItems },
  ];

  return (
    <StatusPaletteContext.Provider value={paScope?.status_legend || []}>
    <div className="mf">
      <WorkspaceMenu current="MANIFEST" />
      <WorkspaceLayout workspace="MANIFEST" navLabel="Manifest review" groups={sidebarGroups}>
      <header className="mf-bar">
        <div className="mf-bar-inner">
          <div className="mf-ident">
            <div className="wsb-title">
            <SidebarTrigger />
            <div className="mf-where">
              <span className="mf-eyebrow">eMerge · Manifest review</span>
              <h1>{detail ? detail.review.facility_name : 'This morning'}</h1>
              <span className="mf-sub">
                {detail
                  ? `Visited ${shortDate(detail.review.visit_date)}${detail.review.provider ? ` · ${detail.review.provider}` : ''}`
                  : `${totals.buildings} workable ${totals.buildings === 1 ? 'building' : 'buildings'}`}
              </span>
            </div>
            </div>
            <div className="mf-bar-acts">
              <FindResidentEntry />
              {/* The end-of-day tally. Offered only to somebody who may create
                  a lead, because recording a day creates work for an advocate. */}
              {!detail && index?.can.create_intake && (
                <button type="button" className={`mf-go${showTwlDay ? ' ghost' : ''}`} onClick={() => setShowTwlDay((s) => !s)}>
                  {showTwlDay ? 'Close' : "Record a day's TWL Q"}
                </button>
              )}
              {detail && (
                <button type="button" className="mf-go ghost" onClick={() => { setUnstartedKey(null); goToReview(null); }}>
                  ← All manifests
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Each desk draws its own <main> and board; the chosen desk settles in. */}
      <ViewTransition viewKey={deskView || 'manifests'}>
      {(deskView === 'leads' || deskView === 'coverage') && <LeadDesk view={deskView} canPlacePaWork={placesPaWork} />}
      {deskView === 'replacements' && <ReplacementDesk />}
      {deskView === 'resubmittals' && <ResubmittalsDesk />}
      {deskView === 'verification' && <VerificationDesk onTotal={setVerificationOpen} />}
      {deskView === 'no-building' && <NoBuildingDesk />}
      {deskView === 'arrivals' && <ArrivalReviewsDesk identity={identityScope} />}
      {deskView === 'records' && <FacilityRecordsDesk />}
      {/* Dark: a typed ?view=previsit with the switch off shows nothing of the desk, and asks the server nothing. */}
      {deskView === 'previsit' && (index?.previsit_enabled
        ? <PrevisitDesk />
        : <main className="mf-main mf-main--board">{indexLoading ? <Skeleton /> : <p>This view is not turned on.</p>}</main>)}

      {deskView === 'unassigned' && (placesPaWork && paScope
        ? <UnassignedDesk scope={paScope} onScopeStale={loadPaScope} />
        : <main className="mf-main mf-main--board"><Skeleton /></main>)}

      {!deskView && (
      <main className="mf-main mf-main--board">
        {!indexRefusal && index && (
          <FindAnotherBuilding onStart={startReview} busyKey={busyStartKey} refusal={startRefusal} />
        )}
        {showTwlDay && !detail && index && (
          <TwlDayForm
            words={index.twl_outcomes || []}
            onClose={() => setShowTwlDay(false)}
            onRecorded={(r, where, names) => {
              setShowTwlDay(false);
              setNotice(twlDaySummary(r, where, names));
              loadIndex();
              // Deliberately does NOT open the review it just recorded.
              // Opening one changes `reviewId`, and the effect that clears
              // the notice on navigation would wipe the summary she just
              // asked for. She also has the next building on her tally to
              // enter, not one review to admire.
            }}
          />
        )}

        {rowless && reviewId && detailRefusal && <StateBlock refusal={detailRefusal} retry={() => loadDetail(reviewId)} />}
        {rowless && reviewId && !detailRefusal && detailLoading && !detail && <Skeleton />}

        <MfBoard<WorkableRow>
          title="Manifests"
          count={index ? totals.buildings : null}
          units={['building', 'buildings']}
          what={<>The buildings a VA can work: Active, or with an open review, or planned from two weeks ago on. Open a row to review its residents, docked beside the table.{totals.undecided > 0 ? ` ${totals.undecided} ${totals.undecided === 1 ? 'resident' : 'residents'} undecided on the open reviews.` : ''}</>}
          notice={(notice || problem) ? (
            <>
              {notice && <p className="mf-notice" role="status">{notice}</p>}
              {problem && <p className="mf-notice mf-notice--stop" role="alert">{problem}</p>}
            </>
          ) : null}
          legend={index && baseRows.length > 0 ? (
            <MfLegend title="Progress key" items={legendItems} whole={baseRows.length} wholeWords="in the table" units={['building', 'buildings']}
              value={legendKey} onSelect={setLegendKey}
              scope="Each entry is a state a building's review is in, with how many buildings are in it. A row says its own count (3 undecided). Press an entry to show only those buildings." />
          ) : null}
          state={state}
          columns={columns}
          identity={identity}
          rows={sorted.rows}
          rowKey={rowKey}
          openKey={openKey}
          onOpenChange={onOpenChange}
          detailSections={sections}
          detailLabel={(r) => `${r.facility_name}: the manifest review`}
          sort={sorted.sort}
          dir={sorted.dir}
          sorts={sorted.sorts}
          onSort={sorted.onSort}
        />
      </main>
      )}
      </ViewTransition>
      </WorkspaceLayout>
    </div>
    </StatusPaletteContext.Provider>
  );
}
